import { LitElement, html, css } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { api } from '../../api';
import { store } from '../../store';
import { showToast } from '../../components/ui/toast';
import { icon } from '../../components/ui/icons';
import { isImageKey } from '../../media-meta';
import { t } from '../../i18n';
import '../../components/ui/album-view';
import type { CadenceAlbumView } from '../../components/ui/album-view';

interface Tag { id: string; name: string; color: string; }
interface Folder { id: string; name: string; album_count?: number; }
interface Album {
  id: string; name: string; folder_id: string | null; description: string | null;
  item_count?: number; pending_count?: number; cover_url?: string | null; cover_type?: 'photo' | 'video' | null;
  cover_item_id?: string | null;
  created_at?: string; last_activity_at?: string | null; taken_min?: string | null; taken_max?: string | null;
}
interface Item {
  id: string; album_id: string; type: 'photo' | 'video'; file_url: string; thumbnail_url: string | null;
  original_filename: string; file_size: number | null; duration: number | null; width: number | null; height: number | null;
  taken_at: string | null; uploader_name: string | null; album_name?: string | null; approved: number; created_at: string; tags?: Tag[];
}

const mediaUrl = (key: string | null | undefined) => (key ? `/api/v1/media/${key}` : '');
function fmtSize(b: number | null): string {
  if (b == null) return '';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}
function fmtDuration(s: number | null): string {
  if (s == null) return '';
  const m = Math.floor(s / 60), sec = s % 60;
  return `${m}:${sec.toString().padStart(2, '0')}`;
}

/**
 * <cadence-media-page> — the admin Media Gallery. Browse folders → albums, then open an
 * album in the shared <cadence-album-view> (upload, selection, lightbox, tags). The
 * Pending tab is the cross-album student-submission approval queue. Gated by media.view
 * (sidebar); manage by media.upload, approve by media.approve.
 */
@customElement('cadence-media-page')
export class CadenceMediaPage extends LitElement {
  @state() private tab: 'gallery' | 'pending' = 'gallery';
  @state() private loading = true;
  @state() private folders: Folder[] = [];
  @state() private albums: Album[] = [];
  @state() private allTags: Tag[] = [];
  @state() private activeFolder = 'all'; // 'all' | folderId | 'unfiled'
  // Date filter: by when the album was last updated (new approved item) or when its photos
  // were taken (EXIF). Empty from/to ⇒ no date filtering.
  @state() private dateBasis: 'updated' | 'taken' = 'updated';
  @state() private dateFrom = '';
  @state() private dateTo = '';

  // Album detail (the open album; <cadence-album-view> fetches + owns its items)
  @state() private openAlbum: Album | null = null;

  // Pending queue
  @state() private pending: Item[] = [];
  @state() private pendingCount = 0;

  // Lightbox (Pending tab only — the album view has its own)
  @state() private lightbox: Item | null = null;
  @state() private lbFull = false;

  // Modals
  @state() private albumModalOpen = false;
  @state() private albumEditId: string | null = null;
  @state() private albumForm = { name: '', folder_id: '', description: '' };
  @state() private folderModalOpen = false;
  @state() private newFolderName = '';
  @state() private saving = false;

  // Confirm (folder delete + pending reject)
  @state() private confirmOpen = false;
  @state() private confirmBusy = false;
  @state() private confirmWithReason = false;
  @state() private confirmConfig: { heading: string; message: string; confirmLabel: string } | null = null;
  private confirmAction: (() => Promise<void>) | null = null;

  private readonly canUpload = store.hasPermission('media.upload');
  private readonly canApprove = store.hasPermission('media.approve');

  static styles = css`
    :host { display: block; }
    .head { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: var(--space-4); flex-wrap: wrap; }
    h1 { margin: 0; font-size: 1.5rem; }
    .tabs { display: flex; gap: 4px; border-bottom: 1px solid var(--color-border); margin-bottom: var(--space-4); }
    .tab { padding: 8px 14px; background: none; border: none; border-bottom: 2px solid transparent; cursor: pointer; color: var(--color-text-secondary); font: inherit; font-weight: 500; display: inline-flex; align-items: center; gap: 6px; }
    .tab.active { color: var(--color-primary); border-bottom-color: var(--color-primary); }
    .count-badge { background: var(--color-danger); color: #fff; border-radius: 999px; font-size: 0.7rem; padding: 1px 7px; font-weight: 700; }
    .bar { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin-bottom: var(--space-4); }
    .chips { display: flex; gap: 6px; flex-wrap: wrap; flex: 1; }
    .chip { display: inline-flex; align-items: center; gap: 6px; padding: 4px 12px; border-radius: 999px; border: 1px solid var(--color-border); background: var(--color-bg-primary); color: var(--color-text-secondary); cursor: pointer; font-size: 0.82rem; }
    .chip.active { background: var(--color-primary); color: #fff; border-color: var(--color-primary); }
    .chip .x { opacity: 0.7; font-weight: 700; } .chip .x:hover { opacity: 1; }
    .filterbar { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin-bottom: var(--space-4); font-size: 0.85rem; }
    .filterbar .fl { color: var(--color-text-secondary); }
    .filterbar select, .filterbar input { font: inherit; font-size: 0.85rem; padding: 5px 8px; border: 1px solid var(--color-border); border-radius: var(--radius-md); background: var(--color-bg-primary); color: var(--color-text-primary); }
    .filterbar .clear { background: none; border: none; color: var(--color-primary); cursor: pointer; font: inherit; font-size: 0.85rem; padding: 5px 4px; }
    .grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(180px, 1fr)); gap: 14px; }
    .album-card { border: 1px solid var(--color-border); border-radius: var(--radius-md); overflow: hidden; cursor: pointer; background: var(--color-bg-primary); transition: border-color 0.15s, transform 0.1s; position: relative; }
    .album-card:hover { border-color: var(--color-primary); }
    .album-cover { aspect-ratio: 4/3; background: var(--color-bg-secondary); display: grid; place-items: center; color: var(--color-text-secondary); overflow: hidden; position: relative; }
    .album-cover img, .album-cover video { width: 100%; height: 100%; object-fit: cover; }
    .album-cover svg { width: 38px; height: 38px; opacity: 0.5; }
    .album-cover .cover-play { position: absolute; inset: 0; display: grid; place-items: center; pointer-events: none; }
    .album-cover .cover-play span { background: rgba(0,0,0,0.5); color: #fff; border-radius: 999px; width: 34px; height: 34px; display: grid; place-items: center; }
    .album-cover .cover-play svg { width: 18px; height: 18px; opacity: 1; }
    .album-meta { padding: 10px 12px; }
    .album-meta .name { font-weight: 600; word-break: break-word; }
    .album-meta .sub { color: var(--color-text-secondary); font-size: 0.78rem; margin-top: 2px; }
    .pending-pill { position: absolute; top: 8px; right: 8px; background: var(--color-danger); color: #fff; border-radius: 999px; font-size: 0.7rem; padding: 2px 8px; font-weight: 700; }
    /* Pending list */
    .pend-item { display: flex; gap: 14px; align-items: center; padding: 12px 0; border-bottom: 1px solid var(--color-border); }
    .pend-thumb { width: 90px; height: 90px; border-radius: var(--radius-md); overflow: hidden; background: var(--color-bg-secondary); flex-shrink: 0; display: grid; place-items: center; cursor: pointer; }
    .pend-thumb img, .pend-thumb video { width: 100%; height: 100%; object-fit: cover; }
    .pend-thumb svg { width: 28px; height: 28px; opacity: 0.5; }
    .pend-info { flex: 1; min-width: 0; }
    .pend-info .fn { font-weight: 600; word-break: break-word; }
    .pend-info .sub { color: var(--color-text-secondary); font-size: 0.8rem; margin-top: 2px; }
    .pend-acts { display: flex; gap: 6px; flex-shrink: 0; }
    /* Lightbox (Pending) */
    .lb-stage { position: relative; display: grid; place-items: center; background: var(--color-bg-secondary); border-radius: var(--radius-md); max-height: 60vh; overflow: hidden; }
    .lb-stage img, .lb-stage video { max-width: 100%; max-height: 60vh; object-fit: contain; }
    .lb-preload { position: absolute; width: 1px; height: 1px; opacity: 0; pointer-events: none; }
    .lb-footer { display: flex; flex: 1; flex-wrap: wrap; gap: 10px; align-items: center; justify-content: space-between; }
    .lb-group { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
    .lb-meta { margin-top: var(--space-3); color: var(--color-text-secondary); font-size: 0.85rem; }
    .tag-block { margin-top: 12px; }
    .tag-block label { display: block; font-size: 0.82rem; color: var(--color-text-secondary); margin-bottom: 4px; }
    .efield { margin-bottom: var(--space-3); }
    .efield label { display: block; font-size: 0.82rem; color: var(--color-text-secondary); margin-bottom: 4px; }
    textarea { width: 100%; box-sizing: border-box; font: inherit; padding: 8px 10px; border: 1px solid var(--color-border); border-radius: var(--radius-md); background: var(--color-bg-primary); color: var(--color-text-primary); resize: vertical; min-height: 70px; }
  `;

  connectedCallback(): void { super.connectedCallback(); this.load(); }

  private async load() {
    this.loading = true;
    try {
      const [folders, albums, tags] = await Promise.all([
        api.get<Folder[]>('/gallery/folders'),
        api.get<Album[]>('/gallery/albums'),
        api.get<Tag[]>('/gallery/tags').catch(() => []),
      ]);
      this.folders = folders; this.albums = albums; this.allTags = tags;
      this.pendingCount = albums.reduce((n, a) => n + (a.pending_count ?? 0), 0);
    } catch (e) { showToast((e as Error).message, 'error'); }
    finally { this.loading = false; }
  }

  private get visibleAlbums(): Album[] {
    let list = this.albums;
    if (this.activeFolder === 'unfiled') list = list.filter((a) => !a.folder_id);
    else if (this.activeFolder !== 'all') list = list.filter((a) => a.folder_id === this.activeFolder);
    if (this.dateFrom || this.dateTo) list = list.filter((a) => this.albumMatchesDate(a));
    return list;
  }

  /** Date-range match. 'updated' compares the album's last approved-item date; 'taken'
   *  keeps any album whose photo "date taken" span overlaps the selected range. */
  private albumMatchesDate(a: Album): boolean {
    const from = this.dateFrom, to = this.dateTo;
    if (this.dateBasis === 'taken') {
      const lo = (a.taken_min ?? '').slice(0, 10), hi = (a.taken_max ?? '').slice(0, 10);
      if (!lo || !hi) return false; // no EXIF dates ⇒ excluded while filtering by date taken
      if (from && hi < from) return false;
      if (to && lo > to) return false;
      return true;
    }
    const d = (a.last_activity_at ?? a.created_at ?? '').slice(0, 10);
    if (!d) return false;
    if (from && d < from) return false;
    if (to && d > to) return false;
    return true;
  }

  private clearDateFilter() { this.dateFrom = ''; this.dateTo = ''; }

  // ---- Album detail ----
  private openAlbumView(a: Album) { this.openAlbum = a; }
  private closeAlbumView() { this.openAlbum = null; this.load(); }
  private mergeTag(tag: Tag) {
    if (!this.allTags.some((t) => t.id === tag.id)) {
      this.allTags = [...this.allTags, tag].sort((a, b) => a.name.localeCompare(b.name));
    }
  }

  // ---- Pending queue ----
  private async loadPending() {
    try { this.pending = await api.get<Item[]>('/gallery/pending'); this.pendingCount = this.pending.length; }
    catch (e) { showToast((e as Error).message, 'error'); }
  }
  private openLightbox(it: Item) { this.lightbox = it; this.lbFull = false; }
  private downloadItem(it: Item) {
    const a = document.createElement('a');
    a.href = mediaUrl(it.file_url);
    a.download = it.original_filename || 'download';
    document.body.appendChild(a); a.click(); a.remove();
  }
  private openItem(it: Item) { window.open(mediaUrl(it.file_url), '_blank', 'noopener'); }

  private async saveItemTags(it: Item, tagIds: string[]) {
    try {
      const updated = await api.put<Item>(`/gallery/items/${it.id}/tags`, { tag_ids: tagIds });
      this.pending = this.pending.map((x) => (x.id === it.id ? { ...x, tags: updated.tags } : x));
      if (this.lightbox?.id === it.id) this.lightbox = { ...this.lightbox, tags: updated.tags };
    } catch (e) { showToast((e as Error).message, 'error'); }
  }
  private addTag(it: Item, tag: Tag) {
    const ids = new Set((it.tags ?? []).map((t) => t.id)); ids.add(tag.id);
    this.saveItemTags(it, [...ids]);
  }
  private removeTag(it: Item, tagId: string) {
    this.saveItemTags(it, (it.tags ?? []).map((t) => t.id).filter((id) => id !== tagId));
  }
  private async createTag(it: Item, name: string) {
    try {
      const tag = await api.post<Tag>('/gallery/tags', { name });
      this.mergeTag(tag);
      this.addTag(it, tag);
    } catch (e) { showToast((e as Error).message, 'error'); }
  }
  private async approve(it: Item) {
    try {
      await api.post(`/gallery/items/${it.id}/approve`, { tag_ids: (it.tags ?? []).map((t) => t.id) });
      showToast(t('media.pending.approved'), 'success');
      await this.loadPending();
      if (this.lightbox?.id === it.id) this.lightbox = null;
    } catch (e) { showToast((e as Error).message, 'error'); }
  }
  private reject(it: Item) {
    this.askConfirm({ heading: t('media.pending.rejectHeading'), message: t('media.pending.rejectMessage', { name: it.original_filename }), confirmLabel: t('media.pending.rejectConfirm') }, async () => {
      const reason = (this.renderRoot.querySelector('#reject-reason') as HTMLTextAreaElement | null)?.value?.trim() || undefined;
      await api.post(`/gallery/items/${it.id}/reject`, { reason });
      this.lightbox = null;
      await this.loadPending();
    }, true);
  }

  // ---- Folders ----
  private async saveFolder() {
    const name = this.newFolderName.trim();
    if (!name) { showToast(t('media.folders.nameRequired'), 'error'); return; }
    this.saving = true;
    try { await api.post('/gallery/folders', { name }); this.folderModalOpen = false; await this.load(); showToast(t('media.folders.created'), 'success'); }
    catch (e) { showToast((e as Error).message, 'error'); }
    finally { this.saving = false; }
  }
  private deleteFolder(f: Folder) {
    this.askConfirm({ heading: t('media.folders.deleteHeading'), message: t('media.folders.deleteMessage', { name: f.name }), confirmLabel: t('media.folders.deleteConfirm') }, async () => {
      await api.del(`/gallery/folders/${f.id}`);
      if (this.activeFolder === f.id) this.activeFolder = 'all';
      await this.load();
    });
  }

  // ---- Albums CRUD ----
  private openNewAlbum() {
    this.albumEditId = null;
    this.albumForm = { name: '', folder_id: this.activeFolder !== 'all' && this.activeFolder !== 'unfiled' ? this.activeFolder : '', description: '' };
    this.albumModalOpen = true;
  }
  private openEditAlbum(a: Album) {
    this.albumEditId = a.id;
    this.albumForm = { name: a.name, folder_id: a.folder_id ?? '', description: a.description ?? '' };
    this.albumModalOpen = true;
  }
  private async saveAlbum() {
    if (!this.albumForm.name.trim()) { showToast(t('media.albums.nameRequired'), 'error'); return; }
    this.saving = true;
    const body = { name: this.albumForm.name.trim(), folder_id: this.albumForm.folder_id || null, description: this.albumForm.description.trim() || null };
    try {
      if (this.albumEditId) {
        const updated = await api.put<Album>(`/gallery/albums/${this.albumEditId}`, body);
        if (this.openAlbum?.id === this.albumEditId) {
          this.openAlbum = { ...this.openAlbum, ...updated };
          (this.renderRoot.querySelector('cadence-album-view') as CadenceAlbumView | null)?.reload();
        }
      } else {
        await api.post('/gallery/albums', body);
      }
      this.albumModalOpen = false;
      await this.load();
      showToast(t('media.albums.saved'), 'success');
    } catch (e) { showToast((e as Error).message, 'error'); }
    finally { this.saving = false; }
  }

  // ---- Confirm dialog ----
  private askConfirm(config: { heading: string; message: string; confirmLabel: string }, action: () => Promise<void>, withReason = false) {
    this.confirmConfig = config; this.confirmAction = action; this.confirmWithReason = withReason; this.confirmOpen = true;
  }
  private closeConfirm() { if (this.confirmBusy) return; this.confirmOpen = false; this.confirmAction = null; }
  private async runConfirm() {
    if (!this.confirmAction) return;
    this.confirmBusy = true;
    try { await this.confirmAction(); this.confirmOpen = false; this.confirmAction = null; }
    catch (e) { showToast((e as Error).message, 'error'); }
    finally { this.confirmBusy = false; }
  }

  private switchTab(tab: 'gallery' | 'pending') {
    this.tab = tab;
    if (tab === 'pending') this.loadPending();
    else this.load();
  }

  render() {
    if (this.loading) return html`<cadence-loading></cadence-loading>`;
    return html`
      <div class="head"><h1>${t('media.title')}</h1></div>
      <div class="tabs">
        <button class="tab ${this.tab === 'gallery' ? 'active' : ''}" @click=${() => this.switchTab('gallery')}>${icon('image', 18)} ${t('media.tabs.gallery')}</button>
        ${this.canApprove ? html`
          <button class="tab ${this.tab === 'pending' ? 'active' : ''}" @click=${() => this.switchTab('pending')}>
            ${icon('clock', 18)} ${t('media.tabs.pending')} ${this.pendingCount > 0 ? html`<span class="count-badge">${this.pendingCount}</span>` : ''}
          </button>` : ''}
      </div>
      ${this.tab === 'gallery' ? (this.openAlbum ? this.renderAlbumView() : this.renderAlbumsList()) : this.renderPending()}
      ${this.renderLightbox()}
      ${this.renderModals()}
    `;
  }

  private renderDateFilter() {
    return html`
      <div class="filterbar">
        <span class="fl">${t('media.filters.filterBy')}</span>
        <select class="basis" .value=${this.dateBasis} @change=${(e: Event) => (this.dateBasis = (e.target as HTMLSelectElement).value as 'updated' | 'taken')}>
          <option value="updated">${t('media.filters.lastUpdated')}</option>
          <option value="taken">${t('media.filters.dateTaken')}</option>
        </select>
        <input type="date" .value=${this.dateFrom} @change=${(e: Event) => (this.dateFrom = (e.target as HTMLInputElement).value)} aria-label=${t('media.filters.fromDate')} />
        <span class="fl">${t('media.filters.to')}</span>
        <input type="date" .value=${this.dateTo} @change=${(e: Event) => (this.dateTo = (e.target as HTMLInputElement).value)} aria-label=${t('media.filters.toDate')} />
        ${this.dateFrom || this.dateTo ? html`<button class="clear" @click=${() => this.clearDateFilter()}>${t('media.filters.clear')}</button>` : ''}
      </div>`;
  }

  /** Album cover: a photo renders as <img>; a video-only album renders its first frame
   *  via <video> (same trick as the item tiles), with a play overlay. Empty → icon. */
  private renderCover(a: Album) {
    if (!a.cover_url) return icon('image', 38);
    const inner = isImageKey(a.cover_url)
      ? html`<img src=${mediaUrl(a.cover_url)} alt=${a.name} loading="lazy" />`
      : html`<video src=${mediaUrl(a.cover_url)} preload="metadata" muted></video>`;
    return html`${inner}${a.cover_type === 'video' ? html`<div class="cover-play"><span>${icon('play', 18)}</span></div>` : ''}`;
  }

  private renderAlbumsList() {
    const hasUnfiled = this.albums.some((a) => !a.folder_id);
    return html`
      <div class="bar">
        <div class="chips">
          <span class="chip ${this.activeFolder === 'all' ? 'active' : ''}" @click=${() => (this.activeFolder = 'all')}>${t('media.albums.all', { count: this.albums.length })}</span>
          ${this.folders.map((f) => html`
            <span class="chip ${this.activeFolder === f.id ? 'active' : ''}" @click=${() => (this.activeFolder = f.id)}>
              ${icon('folder', 14)} ${f.name}
              ${this.canUpload ? html`<span class="x" title=${t('media.albums.deleteFolderTitle')} @click=${(e: Event) => { e.stopPropagation(); this.deleteFolder(f); }}>×</span>` : ''}
            </span>`)}
          ${hasUnfiled ? html`<span class="chip ${this.activeFolder === 'unfiled' ? 'active' : ''}" @click=${() => (this.activeFolder = 'unfiled')}>${t('media.unfiled')}</span>` : ''}
        </div>
        ${this.canUpload ? html`
          <cadence-button variant="ghost" size="sm" @click=${() => { this.newFolderName = ''; this.folderModalOpen = true; }}>${t('media.albums.newFolder')}</cadence-button>
          <cadence-button size="sm" @click=${() => this.openNewAlbum()}>${t('media.albums.newAlbum')}</cadence-button>` : ''}
      </div>
      ${this.renderDateFilter()}
      ${this.visibleAlbums.length ? html`
        <div class="grid">
          ${this.visibleAlbums.map((a) => html`
            <div class="album-card" @click=${() => this.openAlbumView(a)}>
              ${this.canApprove && (a.pending_count ?? 0) > 0 ? html`<span class="pending-pill">${t('media.albums.pendingPill', { count: a.pending_count ?? 0 })}</span>` : ''}
              <div class="album-cover">${this.renderCover(a)}</div>
              <div class="album-meta">
                <div class="name">${a.name}</div>
                <div class="sub">${(a.item_count ?? 0) === 1 ? t('media.albums.itemCount', { count: a.item_count ?? 0 }) : t('media.albums.itemCountPlural', { count: a.item_count ?? 0 })}</div>
              </div>
            </div>`)}
        </div>`
        : html`<cadence-empty-state heading=${t('media.albums.emptyHeading')} message=${this.canUpload ? t('media.albums.emptyMessageCanUpload') : t('media.albums.emptyMessage')}></cadence-empty-state>`}
    `;
  }

  private renderAlbumView() {
    const a = this.openAlbum!;
    return html`
      <cadence-album-view
        .apiBase=${'/gallery'}
        .albumId=${a.id}
        .canUpload=${this.canUpload}
        .canApprove=${this.canApprove}
        .canTag=${this.canUpload}
        .canSetCover=${this.canUpload}
        .canEditAlbum=${this.canUpload}
        .canDeleteAlbum=${this.canApprove}
        .showBack=${true}
        .tags=${this.allTags}
        @back=${() => this.closeAlbumView()}
        @edit-album=${() => this.openEditAlbum(a)}
        @album-deleted=${() => this.closeAlbumView()}
        @tag-created=${(e: CustomEvent) => this.mergeTag(e.detail)}></cadence-album-view>`;
  }

  private renderPending() {
    if (!this.pending.length) return html`<cadence-empty-state heading=${t('media.pending.emptyHeading')} message=${t('media.pending.emptyMessage')}></cadence-empty-state>`;
    return html`
      ${this.pending.map((it) => html`
        <div class="pend-item">
          <div class="pend-thumb" @click=${() => this.openLightbox(it)}>
            ${it.type === 'photo' || isImageKey(it.thumbnail_url)
              ? html`<img src=${mediaUrl(it.thumbnail_url || it.file_url)} alt=${it.original_filename} loading="lazy" />`
              : html`<video src=${mediaUrl(it.file_url)} preload="metadata" muted></video>`}
          </div>
          <div class="pend-info">
            <div class="fn">${it.original_filename}</div>
            <div class="sub">${it.type === 'video' ? t('media.type.video') : t('media.type.photo')} · ${it.album_name ?? ''} · ${it.uploader_name || t('media.pending.unknownUploader')} · ${(it.created_at ?? '').slice(0, 10)}</div>
          </div>
          <div class="pend-acts">
            <cadence-button size="sm" @click=${() => this.approve(it)}>${t('media.pending.approve')}</cadence-button>
            <cadence-button variant="danger" size="sm" @click=${() => this.reject(it)}>${t('media.pending.reject')}</cadence-button>
          </div>
        </div>`)}
    `;
  }

  private renderLightbox() {
    const it = this.lightbox;
    if (!it) return '';
    return html`
      <cadence-modal ?open=${!!it} heading=${it.original_filename} @close=${() => (this.lightbox = null)}>
        <div class="lb-stage">
          ${it.type === 'photo'
            ? html`
                <img src=${this.lbFull ? mediaUrl(it.file_url) : mediaUrl(it.thumbnail_url || it.file_url)} alt=${it.original_filename} />
                ${!this.lbFull && it.thumbnail_url && it.thumbnail_url !== it.file_url
                  ? html`<img class="lb-preload" src=${mediaUrl(it.file_url)} @load=${() => (this.lbFull = true)} aria-hidden="true" />`
                  : ''}`
            : html`<video src=${mediaUrl(it.file_url)} controls preload="metadata"></video>`}
        </div>
        <div class="lb-meta">
          ${it.taken_at ? `${t('media.lightbox.taken', { date: it.taken_at.slice(0, 10) })} · ` : ''}
          ${it.type === 'video' && it.duration != null ? `${fmtDuration(it.duration)} · ` : ''}
          ${it.width && it.height ? `${it.width}×${it.height} · ` : ''}
          ${fmtSize(it.file_size)}${it.uploader_name ? ` · ${it.uploader_name}` : ''}
          ${it.approved ? '' : ` · ${t('media.lightbox.awaitingApproval')}`}
        </div>
        ${this.canUpload ? html`
          <div class="tag-block">
            <label>${t('media.lightbox.tags')}</label>
            <cadence-tag-picker
              .selected=${it.tags ?? []}
              .options=${this.allTags}
              placeholder=${t('media.lightbox.tagPlaceholder')}
              @select=${(e: CustomEvent) => this.addTag(it, e.detail)}
              @create=${(e: CustomEvent) => this.createTag(it, e.detail)}
              @remove=${(e: CustomEvent) => this.removeTag(it, e.detail)}></cadence-tag-picker>
          </div>` : ''}
        <div slot="footer" class="lb-footer">
          <div class="lb-group">
            <cadence-button variant="ghost" @click=${() => this.openItem(it)}>${t('media.lightbox.openFull')}</cadence-button>
            <cadence-button variant="ghost" @click=${() => this.downloadItem(it)}>${t('media.lightbox.download')}</cadence-button>
          </div>
          <div class="lb-group">
            ${!it.approved && this.canApprove ? html`
              <cadence-button variant="danger" @click=${() => this.reject(it)}>${t('media.pending.reject')}</cadence-button>
              <cadence-button @click=${() => this.approve(it)}>${t('media.pending.approve')}</cadence-button>` : ''}
            <cadence-button variant="ghost" @click=${() => (this.lightbox = null)}>${t('media.lightbox.close')}</cadence-button>
          </div>
        </div>
      </cadence-modal>`;
  }

  private renderModals() {
    return html`
      <cadence-modal ?open=${this.albumModalOpen} heading=${this.albumEditId ? t('media.albums.modalEditHeading') : t('media.albums.modalNewHeading')} @close=${() => (this.albumModalOpen = false)}>
        <div class="efield">
          <cadence-input label=${t('media.albums.nameLabel')} .value=${this.albumForm.name} @input=${(e: CustomEvent) => (this.albumForm = { ...this.albumForm, name: e.detail })}></cadence-input>
        </div>
        <div class="efield">
          <label>${t('media.albums.folderLabel')}</label>
          <cadence-select .value=${this.albumForm.folder_id}
            .options=${[{ value: '', label: t('media.unfiled') }, ...this.folders.map((f) => ({ value: f.id, label: f.name }))]}
            @change=${(e: CustomEvent) => (this.albumForm = { ...this.albumForm, folder_id: e.detail })}></cadence-select>
        </div>
        <div class="efield">
          <label>${t('media.albums.descriptionLabel')}</label>
          <textarea .value=${this.albumForm.description} @input=${(e: Event) => (this.albumForm = { ...this.albumForm, description: (e.target as HTMLTextAreaElement).value })}></textarea>
        </div>
        <div slot="footer">
          <cadence-button variant="ghost" @click=${() => (this.albumModalOpen = false)}>${t('common.actions.cancel')}</cadence-button>
          <cadence-button ?loading=${this.saving} @click=${() => this.saveAlbum()}>${t('media.albums.save')}</cadence-button>
        </div>
      </cadence-modal>

      <cadence-modal ?open=${this.folderModalOpen} heading=${t('media.folders.modalHeading')} @close=${() => (this.folderModalOpen = false)}>
        <div class="efield">
          <cadence-input label=${t('media.folders.nameLabel')} .value=${this.newFolderName}
            @input=${(e: CustomEvent) => (this.newFolderName = e.detail as string)}
            @enter=${() => this.saveFolder()}></cadence-input>
        </div>
        <div slot="footer">
          <cadence-button variant="ghost" @click=${() => (this.folderModalOpen = false)}>${t('common.actions.cancel')}</cadence-button>
          <cadence-button ?loading=${this.saving} @click=${() => this.saveFolder()}>${t('media.folders.create')}</cadence-button>
        </div>
      </cadence-modal>

      <cadence-modal ?open=${this.confirmOpen} heading=${this.confirmConfig?.heading ?? ''} @close=${() => this.closeConfirm()}>
        <p style="margin:0">${this.confirmConfig?.message ?? ''}</p>
        ${this.confirmWithReason ? html`<div class="efield" style="margin-top:12px"><textarea id="reject-reason" placeholder=${t('media.pending.reasonPlaceholder')}></textarea></div>` : ''}
        <div slot="footer">
          <cadence-button variant="ghost" ?disabled=${this.confirmBusy} @click=${() => this.closeConfirm()}>${t('common.actions.cancel')}</cadence-button>
          <cadence-button variant="danger" ?loading=${this.confirmBusy} @click=${() => this.runConfirm()}>${this.confirmConfig?.confirmLabel ?? t('media.confirm.fallback')}</cadence-button>
        </div>
      </cadence-modal>
    `;
  }
}
